// GLSL ES 3.00 sources. Colour conversion follows C-01/C-12; compositing follows F-05/F-06/F-16.

export const FULLSCREEN_VS = `#version 300 es
precision highp float;
out vec2 vPos; // 0..1, origin top-left
void main() {
  // Oversized triangle covering the viewport; vPos is 0..1 with the origin at the top-left.
  vec2 p = vec2(float((gl_VertexID << 1) & 2), float(gl_VertexID & 2));
  vPos = p;
  gl_Position = vec4(p.x * 2.0 - 1.0, 1.0 - p.y * 2.0, 0.0, 1.0);
}`;

/**
 * YCbCr planes (integer textures) → R'G'B' in RGBA16F at the source's native resolution.
 * uLayout: 0 = 3 planes (Y, Cb, Cr), 1 = 2 planes (Y, interleaved CbCr).
 * uChromaShift: log2 subsampling per axis (x, y). Chroma upsampling is bilinear with left-cosited horizontal and
 * centred vertical siting (H.264/HEVC default chroma_sample_loc_type 0), identical for every source (C-12).
 */
export const YUV_TO_RGB_FS = `#version 300 es
precision highp float;
precision highp usampler2D;
uniform usampler2D uY;
uniform usampler2D uU; // Cb or interleaved CbCr (RG)
uniform usampler2D uV;
uniform int uLayout;
uniform ivec2 uChromaShift;
uniform float uMaxCode;
uniform mat4 uMatrix; // columns: coefficients for Y, Cb, Cr, offset → rgb in xyz
out vec4 outColor;

vec2 chromaAt(ivec2 c, ivec2 csize) {
  c = clamp(c, ivec2(0), csize - 1);
  if (uLayout == 1) {
    uvec4 t = texelFetch(uU, c, 0);
    return vec2(t.rg);
  }
  return vec2(float(texelFetch(uU, c, 0).r), float(texelFetch(uV, c, 0).r));
}

void main() {
  ivec2 p = ivec2(gl_FragCoord.xy);
  // Framebuffer row r holds image row r (row 0 = top of the image), matching the plane upload order.
  float y = float(texelFetch(uY, p, 0).r);
  ivec2 csize = textureSize(uU, 0);
  vec2 fx = vec2(p) + 0.5;
  // Horizontal: left-cosited → chroma sample j sits at luma x = j * 2^sx.
  float cx = uChromaShift.x == 0 ? float(p.x) : float(p.x) / float(1 << uChromaShift.x);
  // Vertical: centred → chroma row i covers luma rows [i*2^sy, (i+1)*2^sy).
  float cy = uChromaShift.y == 0 ? float(p.y) : fx.y / float(1 << uChromaShift.y) - 0.5;
  ivec2 c0 = ivec2(floor(vec2(cx, cy)));
  vec2 f = vec2(cx, cy) - vec2(c0);
  vec2 c00 = chromaAt(c0, csize);
  vec2 c10 = chromaAt(c0 + ivec2(1, 0), csize);
  vec2 c01 = chromaAt(c0 + ivec2(0, 1), csize);
  vec2 c11 = chromaAt(c0 + ivec2(1, 1), csize);
  vec2 cbcr = mix(mix(c00, c10, f.x), mix(c01, c11, f.x), f.y);
  vec4 yuv1 = vec4(y / uMaxCode, cbcr / uMaxCode, 1.0);
  vec3 rgb = (uMatrix * yuv1).xyz;
  outColor = vec4(rgb, 1.0);
}`;

/** RGBA8 fallback (browser-converted frames and proxies) → RGBA16F. */
export const RGBA_COPY_FS = `#version 300 es
precision highp float;
uniform sampler2D uTex;
uniform mat4 uCorrect; // browser-converted RGB → RGB as our conversion would give (S-03)
out vec4 outColor;
void main() {
  ivec2 p = ivec2(gl_FragCoord.xy);
  vec3 c = texelFetch(uTex, p, 0).rgb;
  outColor = vec4((uCorrect * vec4(c, 1.0)).xyz, 1.0);
}`;

/** 16-bit R'G'B'A (image sequences, F-01.12) → RGBA16F. */
export const RGBA16_COPY_FS = `#version 300 es
precision highp float;
precision highp usampler2D;
uniform usampler2D uTex;
out vec4 outColor;
void main() {
  ivec2 p = ivec2(gl_FragCoord.xy);
  outColor = vec4(vec3(texelFetch(uTex, p, 0).rgb) / 65535.0, 1.0);
}`;

/**
 * Compositing shader for one cell. Maps each device pixel to A coordinates, then to each source's pixel grid via
 * its spatial transform (F-03), applies view adjustments (F-16), the compare mode (F-05) and view aids (F-06.7/8).
 */
export const COMPOSITE_FS = `#version 300 es
precision highp float;
uniform sampler2D uTexA;
uniform sampler2D uTexB;
uniform vec2 uSizeA;      // source A native size (px)
uniform vec2 uSizeB;      // source B native size (px)
uniform vec2 uFrameA;     // A frame size in A pixels (for outside test)
uniform vec4 uXfA;        // A coords → source A pixels: (dx, dy, sx, sy) with srcPx = (a - d) / s
uniform vec4 uXfB;
uniform vec2 uViewOrigin; // A coordinate at the cell's top-left
uniform float uViewScale; // A pixels per device pixel
uniform vec2 uCellSize;   // device px
uniform int uMode;        // 0 single(A tex), 1 wipe, 2 overlay, 3 difference
uniform int uHasA;
uniform int uHasB;
uniform int uNearest;
uniform vec3 uOutsideColor;
uniform vec3 uBgColor;
uniform float uWipePos;
uniform float uWipeAngle;
uniform float uOpacity;
uniform float uDiffGain;
uniform int uDiffHeat;
uniform int uDiffLuma;
uniform int uDiffBand;    // 0 all, 1 low frequencies (light, colour), 2 high frequencies (detail); F-05.6/F-05.7
uniform sampler2D uTexAMip; // the same textures through a mipmapping sampler
uniform sampler2D uTexBMip;
uniform float uBlurA;     // blur size in A pixels
uniform int uChannel;     // 0 rgb, 1 r, 2 g, 3 b, 4 luma
uniform int uTexture;     // grayscale local-contrast view
uniform int uClip;        // clipping overlay (F-07.7)
uniform int uHasRef;      // reference A texture bound (single panes showing a B)
uniform sampler2D uTexRef;
uniform vec2 uSizeRef;
uniform vec4 uXfRef;
uniform int uAdjA;        // adjustment enabled for A / B
uniform int uAdjB;
uniform vec4 uAdjParamsA; // exposure, gamma, _, _
uniform vec4 uAdjParamsB;
uniform float uLevelsA[20]; // master + r + g + b: inBlack, inWhite, outBlack, outWhite, mid
uniform float uLevelsB[20];
in vec2 vPos;
out vec4 outColor;

const vec3 LUMA = vec3(0.2126, 0.7152, 0.0722);

float levels(float v, float l[20], int o) {
  float span = max(1e-6, l[o + 1] - l[o]);
  float x = clamp((v - l[o]) / span, 0.0, 1.0);
  x = pow(x, 1.0 / max(0.01, l[o + 4]));
  return l[o + 2] + (l[o + 3] - l[o + 2]) * x;
}

vec3 adjust(vec3 c, vec4 p, float l[20]) {
  c = c * exp2(p.x);
  c = pow(clamp(c, 0.0, 1.0), vec3(1.0 / max(0.01, p.y)));
  c = vec3(levels(c.r, l, 0), levels(c.g, l, 0), levels(c.b, l, 0));
  c = vec3(levels(c.r, l, 5), levels(c.g, l, 10), levels(c.b, l, 15));
  return c;
}

vec3 sampleSrc(sampler2D tex, vec2 size, vec2 px) {
  if (uNearest == 1) {
    ivec2 ip = ivec2(floor(px));
    return texelFetch(tex, clamp(ip, ivec2(0), ivec2(size) - 1), 0).rgb;
  }
  return texture(tex, px / size).rgb;
}

// Returns rgb, and sets inside=false when the position falls outside the source's pixels.
vec3 fetchSrc(sampler2D tex, vec2 size, vec4 xf, vec2 a, out bool inside) {
  vec2 px = (a - xf.xy) / xf.zw;
  inside = px.x >= 0.0 && px.y >= 0.0 && px.x < size.x && px.y < size.y;
  if (!inside) return vec3(0.0);
  return sampleSrc(tex, size, px);
}

float lumaAt(sampler2D tex, vec2 size, vec4 xf, vec2 a) {
  bool ins;
  return dot(fetchSrc(tex, size, xf, a, ins), LUMA);
}

vec3 textureView(sampler2D tex, vec2 size, vec4 xf, vec2 a, vec3 c) {
  // Local contrast: luma minus a ring-average blur (radius ~3 source px), strongly amplified (F-06.8).
  float l = dot(c, LUMA);
  float r = 3.0 * xf.z;
  float s = 0.0;
  for (int i = 0; i < 12; i++) {
    float ang = float(i) * 0.5235988;
    s += lumaAt(tex, size, xf, a + vec2(cos(ang), sin(ang)) * r);
    s += lumaAt(tex, size, xf, a + vec2(cos(ang), sin(ang)) * r * 0.5);
  }
  float blur = s / 24.0;
  return vec3(clamp(0.5 + (l - blur) * 4.0, 0.0, 1.0));
}

// Blurred source colour at A position a: a mip level whose texels cover uBlurA A pixels.
vec3 blurAt(sampler2D tex, vec2 size, vec4 xf, vec2 a) {
  vec2 px = clamp((a - xf.xy) / xf.zw, vec2(0.0), size);
  float lod = max(0.0, log2(uBlurA / max(xf.z, 1e-6)));
  return textureLod(tex, px / size, lod).rgb;
}

vec3 channelView(vec3 c) {
  if (uChannel == 1) return vec3(c.r);
  if (uChannel == 2) return vec3(c.g);
  if (uChannel == 3) return vec3(c.b);
  if (uChannel == 4) return vec3(dot(c, LUMA));
  return c;
}

vec3 heat(float v) {
  v = clamp(v, 0.0, 1.0);
  return clamp(vec3(1.5 - abs(4.0 * v - 3.0), 1.5 - abs(4.0 * v - 2.0), 1.5 - abs(4.0 * v - 1.0)), 0.0, 1.0) * step(0.004, v)
       + vec3(0.0);
}

void main() {
  vec2 cellPx = vPos * uCellSize;
  vec2 a = uViewOrigin + cellPx * uViewScale;
  if (a.x < 0.0 || a.y < 0.0 || a.x >= uFrameA.x || a.y >= uFrameA.y) {
    outColor = vec4(uOutsideColor, 1.0);
    return;
  }
  bool insA = false;
  bool insB = false;
  vec3 ca = vec3(0.0);
  vec3 cb = vec3(0.0);
  vec3 rawA = vec3(0.5);
  vec3 rawB = vec3(0.5);
  if (uHasA == 1) {
    ca = fetchSrc(uTexA, uSizeA, uXfA, a, insA);
    rawA = ca;
    if (uAdjA == 1) ca = adjust(ca, uAdjParamsA, uLevelsA);
    if (uTexture == 1 && insA) ca = textureView(uTexA, uSizeA, uXfA, a, ca);
  }
  if (uMode != 0 && uHasB == 1) {
    cb = fetchSrc(uTexB, uSizeB, uXfB, a, insB);
    rawB = cb;
    if (uAdjB == 1) cb = adjust(cb, uAdjParamsB, uLevelsB);
    if (uTexture == 1 && insB) cb = textureView(uTexB, uSizeB, uXfB, a, cb);
  }
  vec3 c;
  if (uMode == 0) {
    c = uHasA == 1 && insA ? ca : vec3(0.0);
  } else if (uMode == 1) {
    vec2 n = vec2(cos(uWipeAngle), sin(uWipeAngle));
    float d = dot(vPos - 0.5, n) - (uWipePos - 0.5);
    c = d < 0.0 ? (insA ? ca : vec3(0.0)) : (insB ? cb : vec3(0.0));
  } else if (uMode == 2) {
    c = mix(insA ? ca : vec3(0.0), insB ? cb : vec3(0.0), uOpacity);
  } else {
    vec3 da = insA ? ca : vec3(0.0);
    vec3 db = insB ? cb : vec3(0.0);
    if (uDiffBand != 0) {
      // View adjustments do not apply to the split layers: they compare the sources as decoded.
      vec3 la = insA ? blurAt(uTexAMip, uSizeA, uXfA, a) : vec3(0.0);
      vec3 lb = insB ? blurAt(uTexBMip, uSizeB, uXfB, a) : vec3(0.0);
      if (uDiffBand == 1) { da = la; db = lb; }
      else { da = (insA ? rawA : vec3(0.0)) - la; db = (insB ? rawB : vec3(0.0)) - lb; }
    }
    float m;
    if (uDiffLuma == 1) m = abs(dot(da, LUMA) - dot(db, LUMA));
    else { vec3 d3 = abs(da - db); m = (d3.r + d3.g + d3.b) / 3.0; }
    m *= uDiffGain;
    c = uDiffHeat == 1 ? heat(m) : vec3(clamp(m, 0.0, 1.0));
  }
  c = channelView(c);
  if (uClip == 1 && uMode != 2 && uMode != 3) {
    // Which source is visible here, and what A shows at the same place (P-03: highlight clipping B adds).
    bool showB = false;
    if (uMode == 1) {
      vec2 n = vec2(cos(uWipeAngle), sin(uWipeAngle));
      showB = dot(vPos - 0.5, n) - (uWipePos - 0.5) >= 0.0;
    }
    vec3 v = showB ? rawB : rawA;
    bool vin = showB ? insB : insA;
    vec3 ref = rawA;
    bool hasRef = showB;
    if (uMode == 0 && uHasRef == 1) {
      bool insR;
      ref = fetchSrc(uTexRef, uSizeRef, uXfRef, a, insR);
      hasRef = insR;
    }
    float mx = max(v.r, max(v.g, v.b));
    bool hi = vin && max(v.r, max(v.g, v.b)) >= 0.999;
    bool lo = vin && mx <= 0.001;
    if (hi || lo) {
      float rmx = max(ref.r, max(ref.g, ref.b));
      bool refClipped = hi ? rmx >= 0.999 : rmx <= 0.001;
      vec3 col = hasRef && !refClipped ? vec3(1.0, 0.24, 1.0) : hi ? vec3(1.0, 0.27, 0.24) : vec3(0.24, 0.43, 1.0);
      c = mix(c, col, 0.8);
    }
  }
  outColor = vec4(c, 1.0);
}`;
